import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import matrix from '../../data/warmupMatrix.json';
import { topTonicFor } from '../../services/warmupStepPlan';
import { assembleRoutine, swapExercise, removeExercise } from '../../services/warmupRoutineModel';
import { DEFAULT_CELL } from './warmupFormat';

/** Module-level so the default arg is referentially stable across renders. */
const EMPTY_CATALOG = [];

/**
 * The warmup player's brain — routine state, the engine transport, and the
 * reached-top tracker — with no markup of its own.
 *
 * WHY IT EXISTS (2026-09-04, Lesson Dashboard I1 phase 0). This logic used to
 * live inside `WarmupPlayer`, which was fine while the routine list and the
 * transport deck were siblings in one component. I1 puts the deck in a sticky
 * conducting strip under the lesson bar and the routine in the right column —
 * different parts of the tree — so the state has to sit above both.
 *
 * CALL IT ONCE PER SURFACE. Two calls would be two "current exercise" states
 * over one sampler engine, which is the same hazard `LessonDashboard/index.js`
 * already guards by hoisting `useWarmupPlayer()` to the page. The lesson calls
 * this beside it and hands the result down; the Warmups page calls it inside
 * `WarmupPlayer`, which still composes the three regions itself.
 *
 * TWO MODES OF OWNERSHIP, unchanged by the move:
 *   - UNCONTROLLED (the Warmups page): the hook owns cell, routine and current;
 *     edits die with the page.
 *   - CONTROLLED (the Lesson Dashboard): `routine`, `current` and their setters
 *     come from `useWarmupRoutine`, with `onExerciseStart` / `onExerciseEnd` so
 *     the lesson saves a first routine on first Play and logs every exercise.
 *
 * THE REACHED TOP. While an exercise plays, the highest UN-shifted tonic is
 * tracked (tonic minus octaveShift — a drop then a climb is not a new top).
 * When the exercise ends, is moved past, or is stopped, `onExerciseEnd` reports
 * it and the lesson folds it into the draft.
 */
export default function useWarmupRoutinePlayer({
	catalog = EMPTY_CATALOG,
	player,
	initialCell = DEFAULT_CELL,
	// controlled mode (all optional; provide together)
	routine: routineProp = null,
	onRoutineChange = null,
	onCellChange = null,
	current: currentProp = null,
	onCurrentChange = null,
	onExerciseStart = null,
	onExerciseEnd = null,
	/**
	 * null = infer from (routine && onRoutineChange), which is what WarmupPlayer
	 * has always done. The Lesson Dashboard passes `true` EXPLICITLY, because it
	 * calls this hook at page level where the routine is still null for the first
	 * few frames: inference would read that as uncontrolled, assemble a throwaway
	 * local routine from an empty catalog, and then flip mode under itself once
	 * the real one arrived. Saying so up front is cheaper than making a mode
	 * switch mid-life correct.
	 */
	controlled: controlledProp = null,
}) {
	const { engine, state, loading, loadError, ensureLoaded, setOnEnd } = player;
	const byId = useMemo(() => new Map(catalog.map((c) => [c.id, c])), [catalog]);
	const controlled = controlledProp === null ? Boolean(routineProp && onRoutineChange) : Boolean(controlledProp);

	// Uncontrolled AND a catalog to build from — otherwise there is nothing to
	// assemble yet and null is the honest answer (`items` already falls back).
	const [localRoutine, setLocalRoutine] = useState(() => (controlled || !catalog.length ? null : assembleRoutine(initialCell, catalog)));
	const [localCurrent, setLocalCurrent] = useState(0);
	const [edited, setEdited] = useState(false);
	const [swapping, setSwapping] = useState(null); // index whose picker is open
	const [routineDone, setRoutineDone] = useState(false);
	const advanceTimer = useRef(null);

	const routine = controlled ? routineProp : localRoutine;
	const cell = routine?.cell || initialCell;
	const current = controlled && Number.isInteger(currentProp) ? currentProp : localCurrent;
	const setCurrent = useCallback(
		(i) => {
			if (controlled && onCurrentChange) onCurrentChange(i);
			else setLocalCurrent(i);
		},
		[controlled, onCurrentChange]
	);
	const setRoutine = useCallback(
		(next) => {
			if (controlled) onRoutineChange(next);
			else setLocalRoutine(next);
		},
		[controlled, onRoutineChange]
	);

	// Memoised: a fresh [] on every render would re-subscribe the onEnd effect
	// below each time — and its cleanup clears the auto-advance timer.
	const items = useMemo(() => routine?.items || [], [routine]);

	// ---- the reached-top tracker -------------------------------------------
	const runRef = useRef(null);
	const finishRun = useCallback(
		(skipped = false) => {
			const run = runRef.current;
			if (!run) return;
			runRef.current = null;
			if (onExerciseEnd) {
				onExerciseEnd({
					index: run.index,
					exerciseId: run.exerciseId,
					tonicStart: run.tonicStart,
					topReached: run.steps > 0 ? run.maxUnshifted : null,
					steps: run.steps,
					octaveShift: run.octaveShift,
					guideLevel: run.guideLevel,
					seconds: (Date.now() - run.startedAt) / 1000,
					skipped,
				});
			}
		},
		[onExerciseEnd]
	);

	useEffect(() => {
		const run = runRef.current;
		if (!run || state.exerciseId !== run.exerciseId) return;
		if (state.status !== 'playing' && state.status !== 'paused') return;
		if (Number.isFinite(state.tonic)) {
			run.maxUnshifted = Math.max(run.maxUnshifted, state.tonic - (state.octaveShift || 0));
		}
		run.steps = Math.max(run.steps, (state.rep || 0) + 1);
		run.octaveShift = state.octaveShift || 0;
		run.guideLevel = state.guideLevel;
	}, [state]);

	// UNMOUNT ONLY. Keyed on a ref, not on `finishRun`: its identity follows
	// `onExerciseEnd`, which the lesson hook re-creates after the first-Play
	// save — and an effect keyed on it ran this cleanup mid-exercise, ending the
	// run at zero steps before a single rep had been counted (live check,
	// 2026-09-03: the log carried topReached null and the draft never moved).
	// The split into three regions multiplies callback identities, so this ref
	// keying matters MORE now, not less.
	const finishRef = useRef(finishRun);
	finishRef.current = finishRun;
	useEffect(() => () => finishRef.current(false), []);

	// ---- cell / rebuild ----------------------------------------------------
	const rebuild = useCallback(
		(nextCell) => {
			finishRun(false);
			engine.stop();
			if (controlled && onCellChange) onCellChange(nextCell);
			else setLocalRoutine(assembleRoutine(nextCell, catalog));
			setEdited(false);
			setCurrent(0);
			setSwapping(null);
			setRoutineDone(false);
		},
		[catalog, controlled, onCellChange, engine, finishRun, setCurrent]
	);

	const changeCell = (key, value) => rebuild({ ...cell, [key]: value });

	// ---- playing -----------------------------------------------------------
	const playItem = useCallback(
		async (i, { countIn = i === 0 } = {}) => {
			const item = items[i];
			const ex = byId.get(item?.exerciseId);
			if (!ex) return;
			clearTimeout(advanceTimer.current);
			finishRun(false);
			const ok = await ensureLoaded();
			if (!ok) return;
			if (onExerciseStart) await onExerciseStart(i);
			setCurrent(i);
			setRoutineDone(false);
			runRef.current = { index: i, exerciseId: ex.id, tonicStart: item.startTonic, maxUnshifted: item.startTonic, steps: 0, octaveShift: 0, guideLevel: state.guideLevel, startedAt: Date.now() };
			engine.play(ex, {
				startTonic: item.startTonic,
				top: topTonicFor(ex, item.startTonic, cell.ageBand, matrix),
				span: item.span,
				bpm: item.bpm,
				countIn,
			});
		},
		[items, byId, ensureLoaded, engine, cell.ageBand, finishRun, onExerciseStart, setCurrent, state.guideLevel]
	);

	// When an exercise finishes its descent: report it, then the rest bar, then the next row.
	useEffect(() => {
		setOnEnd(() => {
			finishRun(false);
			const nextIndex = current + 1;
			if (nextIndex >= items.length) {
				setRoutineDone(true);
				return;
			}
			const restSeconds = (matrix.score.restBarBeats * 60) / (items[nextIndex].bpm || 80);
			advanceTimer.current = setTimeout(() => playItem(nextIndex, { countIn: false }), restSeconds * 1000);
		});
		return () => clearTimeout(advanceTimer.current);
	}, [current, items, playItem, setOnEnd, finishRun]);

	const onPlayPause = () => {
		if (state.status === 'playing') engine.pause();
		else if (state.status === 'paused') engine.resume();
		else playItem(current);
	};

	const stopCurrent = (skipped) => {
		finishRun(skipped);
		engine.stop();
	};

	const onSkip = (i) => {
		const next = removeExercise(routine, i, catalog);
		if (i === current) stopCurrent(true);
		setRoutine(next);
		setEdited(true);
		setSwapping(null);
		if (i < current) setCurrent(current - 1);
		else if (i === current && current >= next.items.length) setCurrent(Math.max(0, next.items.length - 1));
	};

	const onSwap = (i, exerciseId) => {
		if (i === current) stopCurrent(false);
		setRoutine(swapExercise(routine, i, exerciseId, catalog));
		setEdited(true);
		setSwapping(null);
	};

	const isActive = state.status === 'playing' || state.status === 'paused';
	const currentEx = byId.get(items[current]?.exerciseId);
	const playLabel = loading ? 'Loading piano…' : state.status === 'playing' ? 'Pause' : state.status === 'paused' ? 'Resume' : 'Play';
	const showRebuild = controlled ? false : edited;

	return {
		// data
		catalog,
		routine,
		cell,
		items,
		byId,
		current,
		currentEx,
		state,
		engine,
		// status
		loading,
		loadError,
		isActive,
		routineDone,
		playLabel,
		showRebuild,
		controlled,
		// actions
		playItem,
		onPlayPause,
		onSkip,
		onSwap,
		changeCell,
		rebuild,
		// the row picker
		swapping,
		setSwapping,
	};
}
